/**
 * The title scene (08 §1), in the arcade title's spirit: the BUBBLE BOBBLE logo across the top in big
 * glossy bubble letters, popping in and then bobbing; Bub and Bob standing either side of the menu,
 * breathing and blinking; glass bubbles drifting up behind them; stars far back. The menu is drawn
 * over the middle by the HUD (`TITLE_LAYOUT`): the logo stays above it and the dragons beside it.
 */

import { Group, Mesh } from 'three';
import { buildDragon } from '../actors/dragon';
import { buildLogo, type Logo, poseLogo } from '../actors/logo';
import type { Puppet } from '../actors/puppet';
import type { Palette } from '../palette';
import { buildFloor, buildStars, buildWall } from './backdrop';
import { TITLE_SCENE } from './layout';
import type { TitleMaterials } from './materials';
import { RisingBubbles } from './risingBubbles';
import { poseIdle } from './titlePoses';

/** Half the menu band's width in tiles, plus a margin: the bubbles' lanes start outside it. */
const LANE_MIN = 9;

export class TitleStage {
  readonly group = new Group();
  private readonly logo: Logo;
  private readonly bub: Puppet;
  private readonly bob: Puppet;
  private readonly bubbles: RisingBubbles;
  private readonly meshes: Mesh[] = [];

  constructor(p: Palette, m: TitleMaterials) {
    const stars = buildStars(p, m.glow, {
      count: 150, seed: 0x7171e, layers: 3, z0: -1.9, dz: 0.3, halfW: 23, halfH: 13.5,
      kinds: [
        { colour: 'paper.0', w: 0.1, h: 0.1 },
        { colour: 'water.2', w: 0.14, h: 0.14 },
        { colour: 'bubTrim.2', w: 0.1, h: 0.1 },
      ],
    });
    const floor = new Mesh(buildFloor(p, 'zen', TITLE_SCENE.floorY, TITLE_SCENE.floorHalf), m.block);
    // The dragons' shadows fall on it.
    floor.receiveShadow = true;
    this.meshes.push(new Mesh(buildWall(p, 26, 16), m.wall), ...stars, floor);
    this.logo = buildLogo(p, m.logo);
    this.bub = this.dragon(p, 'bub', m, -1);
    this.bob = this.dragon(p, 'bob', m, 1);
    this.bubbles = new RisingBubbles([m.glassBub, m.glassBob], 12, LANE_MIN, 21);
    this.group.add(...this.meshes, this.logo.root, this.bub.root, this.bob.root, this.bubbles.group);
    this.update(0);
  }

  private dragon(p: Palette, skin: 'bub' | 'bob', m: TitleMaterials, side: -1 | 1): Puppet {
    const pup = buildDragon(p, skin, m.dragon);
    pup.root.position.set(side * TITLE_SCENE.dragonX, TITLE_SCENE.floorY, 0);
    pup.root.scale.setScalar(TITLE_SCENE.dragonScale);
    return pup;
  }

  update(t: number): void {
    poseLogo(this.logo, t);
    // Each faces the middle, where the menu is.
    poseIdle(this.bub, t, 1, 1);
    poseIdle(this.bob, t, -1, 2);
    this.bubbles.update(t);
  }

  dispose(): void {
    for (const mesh of this.meshes) mesh.geometry.dispose();
    for (const g of this.logo.geometries) g.dispose();
    this.bub.dispose();
    this.bob.dispose();
    this.bubbles.dispose();
  }
}
